import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Search, Filter } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_TONE = {
    submitted: "bg-blue-100 text-blue-700",
    approved: "bg-emerald-100 text-emerald-700",
    processing: "bg-amber-100 text-amber-800",
    dispatched: "bg-violet-100 text-violet-700",
    delivered: "bg-emerald-200 text-emerald-800",
    invoiced: "bg-brand-100 text-brand-800",
    paid: "bg-emerald-300 text-emerald-900",
    closed: "bg-zinc-200 text-zinc-700",
    cancelled: "bg-rose-100 text-rose-700",
};

const STATUS_FILTERS = [
    "submitted", "approved", "processing", "dispatched", "delivered", "invoiced", "paid", "closed", "cancelled", "all",
];

const AdminB2BOrders = () => {
    const [status, setStatus] = useState("submitted");
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        const params = {};
        if (status !== "all") params.status = status;
        api.get("/admin/b2b/orders", { params }).then((r) => {
            setItems(r.data.items || []);
            setCounts(r.data.counts || {});
            setTotal(r.data.total || 0);
        }).finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [status]);

    return (
        <AdminAuthGuard>
            <AdminLayout title="B2B orders" subtitle="Wholesale order processing queue">
                <div className="mb-4 flex flex-wrap items-center gap-1.5" data-testid="admin-b2b-orders-tabs">
                    {STATUS_FILTERS.map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatus(s)}
                            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                                status === s ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                            }`}
                            data-testid={`admin-b2b-orders-tab-${s}`}
                        >
                            {s.replace("_", " ")}
                            {counts[s] != null && s !== "all" && (
                                <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[9px] ${status === s ? "bg-white/20" : "bg-zinc-100"}`}>{counts[s]}</span>
                            )}
                        </button>
                    ))}
                </div>
                <p className="mb-3 text-xs text-slate-500">{total} order(s) match this filter</p>

                {loading ? (
                    <div className="h-48 skeleton rounded-2xl" />
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="admin-b2b-orders-empty">
                        <Receipt className="mx-auto h-8 w-8 text-slate-300" />
                        <p className="mt-2">No orders in this view.</p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
                        <table className="w-full text-sm" data-testid="admin-b2b-orders-table">
                            <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                                <tr>
                                    <th className="px-4 py-3">Order #</th>
                                    <th className="px-4 py-3">Account</th>
                                    <th className="px-4 py-3">Items</th>
                                    <th className="px-4 py-3">Terms</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3 text-right">Total</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-50">
                                {items.map((o) => (
                                    <tr key={o.id} className="hover:bg-zinc-50" data-testid={`admin-b2b-orders-row-${o.id}`}>
                                        <td className="px-4 py-3 font-mono text-xs">{o.order_no}</td>
                                        <td className="px-4 py-3 text-xs text-slate-600">{o.b2b_account_id?.slice(0, 8)}…</td>
                                        <td className="px-4 py-3 text-xs">{o.items?.length || 0} · {new Date(o.created_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 text-xs">{(o.payment_terms || "").replace("_", " ")}</td>
                                        <td className="px-4 py-3">
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[o.status]}`}>{o.status.replace("_", " ")}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm font-semibold">{inr(o.amounts?.total)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <Link to={`/admin/b2b/orders/${o.id}`} className="text-xs text-brand-700 hover:underline" data-testid={`admin-b2b-orders-open-${o.id}`}>Open →</Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminB2BOrders;
